import Image from '@base/Image';
import ImageCarousel from '@comp/ImageCarousel/ImageCarousel';
import styled from '@emotion/styled';
import segmentStorage from '@events/segmentStorage';
import { useIsMobile } from '@stores/AppCommonData';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import AquillaButton from 'aquilla/core/molecules/Button';
import { getAnimationPath } from '@utils/animationHelpers';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useRouter } from 'next/router';
import Dynamic from '@base/Dynamic/Dynamic';
import { STORE_FIND_CENTRE_CLICKED } from './constants';

const LottieAnimation = Dynamic(
  () => import('@base/LottieAnimation/LottieAnimation')
);

const Wrapper = styled.div<{ $lastChild: boolean }>`
  width: 100%;
  border-top: 1px solid var(--color-i-divider);
  border-bottom: ${({ $lastChild }) =>
    !$lastChild ? '1px solid var(--color-i-divider);' : 'none;'};
  height: 286px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: 100%;
    padding-bottom: 16px;
  }
`;

const InternalWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background: var(--color-i-white);
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    margin-top: ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const TextContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Spacings.SPACING_4B};
`;

const BannerTitleWrapper = styled.div`
  gap: ${Spacings.SPACING_3B};
`;

const BannerTitle = styled(Typography)`
  width: 246px;
  ${mediaQuery.uptoMobile} {
    width: 210px;
  }
`;

const DetailsWrapperInternal = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    width: 90%;
    justify-content: space-between;
  }
`;

const ClassBatches = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ClassBatchesSubTitle = styled(Typography)`
  color: var(--color-i-gray-13);
`;

const LearnersEnroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LearnersCount = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
`;

const LearnersImageWrapper = styled.div`
  width: 50px;
  height: ${Spacings.SPACING_5B};
  position: relative;
  margin-right: ${Spacings.SPACING_1B};
`;

const AnimationContainer = styled(LottieAnimation)<{ src: string }>`
  width: 50px;
  height: ${Spacings.SPACING_5B};
  flex-shrink: 0;
`;

const LearnersEnrollToday = styled(Typography)`
  color: var(--color-i-gray-13);
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const OfflineCentreImage = styled.div`
  position: relative;
  width: 351px;
  height: 238px;
  .offline-banner-store {
    position: absolute;
    bottom: 10px;
  }
  .indicator-offline-banner-store {
    z-index: 1001;
  }
  & > div:first-child {
    height: 238px;
    border-radius: ${Spacings.SPACING_4B};
    ${mediaQuery.uptoMobile} {
      width: 100%;
      height: unset;
    }
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: unset;
  }
`;

const SubscriptionButton = styled(AquillaButton)`
  background-color: var(--color-i-voilet-23);
  margin-top: ${Spacings.SPACING_4B};
  border-color: var(--color-i-voilet-23);
  height: ${Spacings.SPACING_12B};
  :hover {
    background-color: var(--color-i-voilet-23);
  }
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_3B};
  }
`;

const ButtonWrapper = styled.div`
  margin-top: -132px;
  & > p > span {
    font-weight: 700;
  }
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const OfflineCentreBanner = ({ data }) => {
  const {
    title,
    banners: { banners = {} } = {},
    batchesStartPrice,
    classSubText,
    classText,
    ctaLabel,
    ctaLink,
    learnerEnrolledCount,
    learnerEnrolledText,
    lastChild
  } = data || [];

  const router = useRouter();
  const goalUID = router.query.goalUID as string;
  const goalSegmentData = useGoalEventProps({ goalUID });
  const { webImages = [] } = banners;
  const isMobile = useIsMobile();
  segmentStorage.setItem('lps', 'Store');

  const offlinePageRedirect = () => {
    segment.track(STORE_FIND_CENTRE_CLICKED, {
      ...goalSegmentData,
      last_primary_source: 'Store'
    });
    router.push(`${ctaLink}?selectcity=true`, `${ctaLink}?selectcity=true`);
  };

  const { innerWidth } = window;
  const imageWidth = innerWidth - 32;
  const imageHeight = imageWidth / 1.47;

  if (!data) return <></>;
  return (
    <Wrapper $lastChild={lastChild}>
      <InternalWrapper>
        <TextContentWrapper>
          <BannerTitleWrapper>
            <BannerTitle variant="h4">{title}</BannerTitle>
          </BannerTitleWrapper>
          <DetailsWrapperInternal>
            <ClassBatches>
              <Typography variant="h6">{classText}</Typography>
              <ClassBatchesSubTitle variant="p3">
                {classSubText}
              </ClassBatchesSubTitle>
            </ClassBatches>
            <LearnersEnroll>
              <LearnersCount>
                <LearnersImageWrapper>
                  <AnimationContainer
                    src={getAnimationPath('goalPage/joiningMembers.json')}
                  />
                </LearnersImageWrapper>
                <Typography variant="h6">{learnerEnrolledCount}</Typography>
              </LearnersCount>
              <LearnersEnrollToday variant="p3">
                {learnerEnrolledText}
              </LearnersEnrollToday>
            </LearnersEnroll>
          </DetailsWrapperInternal>
        </TextContentWrapper>
        <OfflineCentreImage>
          <ImageCarousel
            color="var(--color-i-white)"
            className="offline-banner-store"
          >
            {webImages.map((imgSrc: any) => (
              <Image
                src={imgSrc}
                width={isMobile ? imageWidth : '351'}
                height={isMobile ? imageHeight : '238'}
                key={`carousel-${imgSrc}`}
                alt="image"
              />
            ))}
          </ImageCarousel>
        </OfflineCentreImage>
      </InternalWrapper>

      <ButtonWrapper>
        {batchesStartPrice && (
          <Typography variant="p2">
            Batches start from <span>{batchesStartPrice}</span>
          </Typography>
        )}
        <SubscriptionButton
          label={ctaLabel}
          onClick={offlinePageRedirect}
          fullWidth={!!isMobile}
          size="medium"
          variant="brand"
        />
      </ButtonWrapper>
    </Wrapper>
  );
};
export default OfflineCentreBanner;
